import { RigidBody } from '@react-three/rapier';
import * as THREE from 'three';
import { Player } from './Player';
import { Interactable } from './Interactable';
import { useStore } from '../../store/useStore';
import { useMemo } from 'react';

import { EnemyRenderer } from './enemies/EnemyRenderer';
import { ProjectileManager } from './ProjectileManager';
import type { MyRoomState, Player as PlayerSchema } from '../../schema/GameState';
import * as Colyseus from 'colyseus.js';

interface ArenaProps {
    onExit: () => void;
    room: Colyseus.Room<MyRoomState> | null;
    remotePlayers: Map<string, PlayerSchema> | undefined;
}

import { RemotePlayer } from './RemotePlayer';

export const Arena = ({ onExit, room, remotePlayers }: ArenaProps) => {
    const theme = useStore((state) => state.theme);
    const isCyberpunk = theme === 'cyberpunk';
    const sessionPlayer = room?.state.players.get(room.sessionId);
    const initialPos: [number, number, number] = sessionPlayer
        ? [sessionPlayer.x, sessionPlayer.y, sessionPlayer.z]
        : [0, 2, 0];

    return (
        <group>
            {/* Projectiles */}
            <ProjectileManager />

            {/* Enemies */}
            <EnemyRenderer room={room} />

            {/* Remote Players */}
            {remotePlayers && Array.from(remotePlayers.entries()).map(([key, p]) => (
                key !== room?.sessionId && p.currentScene === 'arena' && <RemotePlayer key={key} player={p} />
            ))}

            {/* Arena-specific Lights */}
            {isCyberpunk ? (
                <>
                    <ambientLight intensity={0.5} />
                    <pointLight position={[0, 10, 0]} intensity={2} color="#00ffcc" distance={50} />
                    <pointLight position={[15, 5, 15]} intensity={1} color="#ff00ff" distance={30} />
                    <pointLight position={[-15, 5, -15]} intensity={1} color="#ff00ff" distance={30} />
                </>
            ) : (
                <>
                    <ambientLight intensity={0.1} />
                    {/* Torches */}
                    <pointLight position={[10, 5, 10]} intensity={2} color="orange" distance={20} />
                    <pointLight position={[-10, 5, -10]} intensity={2} color="orange" distance={20} />
                    <pointLight position={[10, 5, -10]} intensity={2} color="orange" distance={20} />
                    <pointLight position={[-10, 5, 10]} intensity={2} color="orange" distance={20} />
                </>
            )}

            <Player
                onMove={(pos, rx) => {
                    if (room) room.send("move", { x: pos.x, y: pos.y, z: pos.z, rx });
                }}
                initialPos={initialPos}
                room={room}
            />

            {/* Exit Interactable */}
            <Interactable
                position={[0, 1, 18]}
                name="Exit Arena"
                color="red"
                onInteract={onExit}
            />

            {/* Environment Geometry */}
            <group>
                {/* Floor */}
                <RigidBody type="fixed" colliders="cuboid" friction={1}>
                    <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
                        <planeGeometry args={[40, 40]} />
                        <meshStandardMaterial
                            color={isCyberpunk ? "#111" : "#2b2b2b"}
                            roughness={isCyberpunk ? 0.2 : 0.9}
                            metalness={isCyberpunk ? 0.8 : 0.1}
                        />
                    </mesh>
                    {/* Cyberpunk Grid */}
                    {isCyberpunk && (
                        <gridHelper args={[40, 40, 0xff00ff, 0x222222]} position={[0, 0.01, 0]} />
                    )}
                </RigidBody>

                {/* Walls */}
                <Walls isCyberpunk={isCyberpunk} />

                {/* Obstacles / Pillars */}
                <Pillars isCyberpunk={isCyberpunk} />
            </group>
        </group>
    );
};

const Walls = ({ isCyberpunk }: { isCyberpunk: boolean }) => {
    const wallHeight = 8;
    const thickness = 2;
    const size = 40;

    const WallMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: isCyberpunk ? "#050510" : "#3b2e2a",
        roughness: isCyberpunk ? 0.1 : 0.9,
        emissive: isCyberpunk ? "#100020" : "#000000"
    }), [isCyberpunk]);

    return (
        <group>
            {/* North */}
            <RigidBody type="fixed" position={[0, wallHeight / 2, -size / 2]}>
                <mesh material={WallMat}>
                    <boxGeometry args={[size, wallHeight, thickness]} />
                </mesh>
                {isCyberpunk && <NeonStrip position={[0, 0, thickness / 2 + 0.1]} width={size} />}
            </RigidBody>
            {/* South */}
            <RigidBody type="fixed" position={[0, wallHeight / 2, size / 2]}>
                {/* Gap for Exit */}
                <group>
                    <mesh position={[-5, 0, 0]} material={WallMat}>
                        <boxGeometry args={[size / 2 - 2, wallHeight, thickness]} />
                    </mesh>
                    <mesh position={[5, 0, 0]} material={WallMat}>
                        <boxGeometry args={[size / 2 - 2, wallHeight, thickness]} />
                    </mesh>
                </group>
            </RigidBody>
            {/* East */}
            <RigidBody type="fixed" position={[size / 2, wallHeight / 2, 0]}>
                <mesh material={WallMat}>
                    <boxGeometry args={[thickness, wallHeight, size]} />
                </mesh>
                {isCyberpunk && <NeonStrip position={[-thickness / 2 - 0.1, 0, 0]} width={size} vertical />}
            </RigidBody>
            {/* West */}
            <RigidBody type="fixed" position={[-size / 2, wallHeight / 2, 0]}>
                <mesh material={WallMat}>
                    <boxGeometry args={[thickness, wallHeight, size]} />
                </mesh>
                {isCyberpunk && <NeonStrip position={[thickness / 2 + 0.1, 0, 0]} width={size} vertical />}
            </RigidBody>
        </group>
    );
};

const NeonStrip = ({ position, width, vertical = false }: { position: [number, number, number], width: number, vertical?: boolean }) => {
    return (
        <mesh position={position} rotation={vertical ? [0, Math.PI / 2, 0] : [0, 0, 0]}>
            <boxGeometry args={[width, 0.2, 0.1]} />
            <meshStandardMaterial color="#00ffcc" emissive="#00ffcc" emissiveIntensity={2} />
        </mesh>
    );
};

const Pillars = ({ isCyberpunk }: { isCyberpunk: boolean }) => {
    const pos = [
        [10, 10], [-10, 10], [10, -10], [-10, -10]
    ];

    return (
        <group>
            {pos.map((p, i) => (
                <RigidBody key={i} type="fixed" position={[p[0], 2.5, p[1]]}>
                    <mesh castShadow receiveShadow>
                        {isCyberpunk ? (
                            <cylinderGeometry args={[1, 1, 5, 16]} />
                        ) : (
                            <boxGeometry args={[2, 5, 2]} />
                        )}
                        <meshStandardMaterial
                            color={isCyberpunk ? "#333" : "#554433"}
                            roughness={0.8}
                        />
                    </mesh>
                </RigidBody>
            ))}
        </group>
    );
}
